import { NetworkStatus } from '@apollo/client';
import { GetCompanyStreamsQuery, StreamsQuerySort, useGetCompanyStreamsQuery } from '@shared/api/graphql/generated';
import { useMemo } from 'react';

interface Props {
  companyId: string;
}

const DEFAULT_PAGINATION = { page: 0, pageSize: 50 };

export type StreamType = NonNullable<GetCompanyStreamsQuery['streamsPaginationQuery']['result']>[0];

// TODO use this only for NewYear banner and remove with old NY banner
const useGetCompanyStreams = (props: Props) => {
  const { companyId } = props;

  const { data, networkStatus, fetchMore } = useGetCompanyStreamsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: { companyIds: [companyId] },
      pagination: DEFAULT_PAGINATION,
      sort: StreamsQuerySort.PointsNumDesc,
    },
    skip: !companyId,
    notifyOnNetworkStatusChange: true,
  });

  const isFetching = networkStatus === NetworkStatus.fetchMore;
  const isLoading = networkStatus === NetworkStatus.loading;
  const pagination = data?.streamsPaginationQuery.paginationInfo;
  const companyStreams = data?.streamsPaginationQuery.result;
  const hasNextPage = pagination?.hasNextPage;

  const { withPoint, withoutPoint } = useMemo(
    () =>
      (companyStreams ?? []).reduce(
        (acc, current) => {
          if (current.pointsNum) {
            acc.withPoint.push(current);
          } else {
            acc.withoutPoint.push(current);
          }

          return acc;
        },
        {
          withPoint: [] as StreamType[],
          withoutPoint: [] as StreamType[],
        }
      ),
    [companyStreams]
  );

  const loadMore = () => {
    if (isLoading || isFetching) {
      return;
    }

    fetchMore({
      variables: {
        filters: { companyIds: [companyId] },
        pagination: { page: (pagination?.page ?? 0) + 1, pageSize: DEFAULT_PAGINATION.pageSize },
        sort: StreamsQuerySort.PointsNumDesc,
      },
      updateQuery: (prev, { fetchMoreResult }) => ({
        ...prev,
        streamsPaginationQuery: {
          paginationInfo: {
            ...prev.streamsPaginationQuery.paginationInfo,
            ...fetchMoreResult.streamsPaginationQuery.paginationInfo,
          },
          result: [
            ...(prev.streamsPaginationQuery.result || []),
            ...(fetchMoreResult.streamsPaginationQuery.result || []),
          ],
        },
      }),
    });
  };

  return {
    companyStreams,
    withPoint,
    withoutPoint,
    hasNextPage,
    isLoading,
    isFetching,
    loadMore,
  };
};

export default useGetCompanyStreams;
